import { useMemo, useState } from "react";
import styles from "./AccountsView.module.scss";
import { Pagination, View, ViewHeader } from "@/components";
import { useAuth } from "@/hooks";
import { AccountsList, AccountsToolbar, CreateAccountDrawer, DeleteAccountModal } from "..";
import { useAccountsQuery } from "../../_hooks/useAccountsQuery";
import { useDeleteAccountMutation } from "../../_hooks/useDeleteAccountMutation";
import { useAccountsStore } from "../../_store/useAccounts.store";
import type { Account } from "../../_types/accounts.types";

export const AccountsView = () => {
  const { user } = useAuth();
  const [isCreateAccountDrawerOpen, setIsCreateAccountDrawerOpen] = useState(false);
  const [accountPendingDelete, setAccountPendingDelete] = useState<Account | null>(null);

  const role = useAccountsStore((state) => state.role);
  const subscription = useAccountsStore((state) => state.subscription);
  const period = useAccountsStore((state) => state.period);
  const search = useAccountsStore((state) => state.search);
  const page = useAccountsStore((state) => state.page);
  const limit = useAccountsStore((state) => state.limit);
  const setRole = useAccountsStore((state) => state.setRole);
  const setSubscription = useAccountsStore((state) => state.setSubscription);
  const setPeriod = useAccountsStore((state) => state.setPeriod);
  const setSearch = useAccountsStore((state) => state.setSearch);
  const setPage = useAccountsStore((state) => state.setPage);
  const setLimit = useAccountsStore((state) => state.setLimit);
  const { data, isLoading, isFetching, isError, refetch } = useAccountsQuery();
  const deleteAccount = useDeleteAccountMutation();

  const accounts = data?.items ?? [];
  const meta = data?.meta ?? {
    total: 0,
    page,
    limit,
    totalPages: 1,
  };

  const deleteModalLabel = useMemo(() => {
    if (!accountPendingDelete) return "";
    const name = [accountPendingDelete.firstName, accountPendingDelete.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return name || accountPendingDelete.email;
  }, [accountPendingDelete]);

  const confirmDeleteAccount = () => {
    if (!accountPendingDelete) return;
    void deleteAccount
      .mutateAsync(accountPendingDelete.id)
      .then(() => setAccountPendingDelete(null))
      .catch(() => {});
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Accounts"
          description="Review account records, activity, and subscription status in one place.<br />Use this section to manage your organization’s user accounts."
        />

        <AccountsToolbar
          role={role}
          subscription={subscription}
          period={period}
          search={search}
          onRoleChange={setRole}
          onSubscriptionChange={setSubscription}
          onPeriodChange={setPeriod}
          onSearchChange={setSearch}
          onCreateAccountClick={() => setIsCreateAccountDrawerOpen(true)}
        />

        <AccountsList
          accounts={accounts}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => refetch()}
          currentUserId={user?.id ?? null}
          actionsDisabled={deleteAccount.isPending}
          onRequestDelete={(account) => setAccountPendingDelete(account)}
        />

        <DeleteAccountModal
          isOpen={accountPendingDelete !== null}
          displayName={deleteModalLabel}
          onClose={() => setAccountPendingDelete(null)}
          onConfirm={confirmDeleteAccount}
          isDeleting={deleteAccount.isPending}
        />

        <CreateAccountDrawer
          isOpen={isCreateAccountDrawerOpen}
          onClose={() => setIsCreateAccountDrawerOpen(false)}
        />

        {meta.total > 0 && (
          <Pagination
            page={meta.page}
            limit={meta.limit}
            total={meta.total}
            totalPages={meta.totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={setLimit}
          />
        )}
      </div>
    </View>
  );
};
